import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FolderTree } from "lucide-react";
import FormHeader from "../../../components/form/FormHeader";
import { useToast } from "../../../components/toast-container";
import { ProgramFormValues, ProgramSection } from "../components/program-period-section";
import { ProgramEditorErrorState } from "../components/program-period-section";
import { OpportunitySection } from "../components/OpportunitySection";
import { useOpportunityDispatcher, useProgramDispatcher, useUpdateProgramData } from "../hooks";
import {
  CreateOpportunityRequest,
  CreateProgramDispatchAction,
  OpportunityDetailDraft,
  OpportunityHierarchyItem,
  UpdateOpportunityRequest,
  UpdateProgramDispatchAction,
} from "../types";
import { type OpportunityFormValues } from "../components/program-period-section/OpportunityEditor";
import { OPPORTUNTY_TYPE } from "../types/campaign-status.constants";

function createOpportunityDraft(opportunity: OpportunityHierarchyItem): OpportunityDetailDraft {
  return {
    type: opportunity.type || "Event",
    description: opportunity.description || "",
    startDate: opportunity.startDate?.slice(0, 10) || "",
    endDate: opportunity.endDate?.slice(0, 10) || "",
  };
}

function UpdateProgramPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { campaignId, programId } = useParams();
  const resolvedCampaignId = campaignId ?? "";
  const [expandedOpportunityId, setExpandedOpportunityId] = useState<string | null>(null);
  const [detailDrafts, setDetailDrafts] = useState<Record<string, OpportunityDetailDraft>>({});

  const {
    campaign,
    selectedProgram,
    selectedProgramId,
    opportunities,
    loading,
    error,
    setSelectedProgramId,
    retry,
  } = useUpdateProgramData(resolvedCampaignId, programId);

  const { dispatch: dispatchProgramAction } = useProgramDispatcher();
  const { dispatch: dispatchOpportunityAction, saving: opportunitySaving } =
    useOpportunityDispatcher();

  useEffect(() => {
    if (location.state && typeof location.state === "object") {
      const state = location.state as {
        selectedProgramId?: string;
        selectedOpportunityId?: string;
      };
      if (state.selectedProgramId) {
        setSelectedProgramId(state.selectedProgramId);
      }
      if (state.selectedOpportunityId) setExpandedOpportunityId(state.selectedOpportunityId);
    }
  }, [location.state, setSelectedProgramId]);

  useEffect(() => {
    if (opportunities.length > 0) {
      setDetailDrafts((current) => {
        const next = { ...current };
        opportunities.forEach((opportunity) => {
          if (!next[opportunity.id]) {
            next[opportunity.id] = createOpportunityDraft(opportunity);
          }
        });
        return next;
      });
    }
  }, [opportunities]);

  const handleBack = () => {
    navigate("/campaign");
  };

  const handleAddProgram = async (values: ProgramFormValues) => {
    try {
      const action: CreateProgramDispatchAction = {
        type: "program/create",
        payload: {
          campaignId: resolvedCampaignId,
          payload: {
            campaignId: resolvedCampaignId,
            name: values.name,
            description: values.description,
            status: values.status,
          },
          onSuccess: () => void retry(),
        },
      };
      await dispatchProgramAction(action);

      addToast({
        type: "success",
        title: "Success!",
        message: "Program created successfully",
      });
    } catch {
      return;
    }
  };

  const handleUpdateProgram = async (programId: string, values: ProgramFormValues) => {
    try {
      const action: UpdateProgramDispatchAction = {
        type: "program/update",
        payload: {
          programId: programId,
          payload: {
            name: values.name,
            description: values.description,
            status: values.status,
          },
          onSuccess: () => void retry(),
        },
      };
      await dispatchProgramAction(action);

      addToast({
        type: "success",
        title: "Success!",
        message: "Program created successfully",
      });
    } catch {
      return;
    }
  };

  const handleCreateOpportunity = async (values: OpportunityFormValues) => {
    if (!selectedProgramId) {
      return;
    }
    const normalizedType = values.type;

    const request: CreateOpportunityRequest = {
      type: normalizedType,
      programId: values.programId ?? "",
    };

    if (values.type === OPPORTUNTY_TYPE.Event) {
      request.eventId = values.sourceId;
    }

    if (values.type === OPPORTUNTY_TYPE.Survey) {
      request.surveyId = values.sourceId;
    }

    await dispatchOpportunityAction({
      type: "opportunity/create",
      payload: {
        programId: selectedProgramId,
        payload: request,
        onSuccess: () => void retry(),
      },
    });

    addToast({
      type: "success",
      title: "Success!",
      message: "Opportunity created successfully",
    });
  };

  const handleUpdateOpportunity = async (opportunityId: string, values: OpportunityFormValues) => {
    if (!selectedProgramId || !opportunityId) {
      return;
    }

    const normalizedType = values.type;

    let payload: CreateOpportunityRequest = {
      type: normalizedType,
      programId: selectedProgramId,
    };

    if (normalizedType === OPPORTUNTY_TYPE.Event) {
      payload.eventId = values.sourceId;
    } else if (normalizedType === OPPORTUNTY_TYPE.Survey) {
      payload.surveyId = values.sourceId;
    }

    await dispatchOpportunityAction({
      type: "opportunity/update",
      payload: {
        programId: selectedProgramId,
        opportunityId,
        payload: payload,
        onSuccess: () => void retry(),
      },
    });

    addToast({
      type: "success",
      title: "Success!",
      message: "Opportunity updated successfully",
    });
  };

  if (!resolvedCampaignId) {
    return (
      <ProgramEditorErrorState
        subtitle="Configure programs and opportunities"
        title="Invalid campaign route."
        description="Open this page with /campaign/:campaignId/program/edit so the campaign can be loaded from the URL."
        error="Missing campaignId from route params."
        onRetry={() => navigate("/campaign")}
        onBack={() => navigate("/campaign")}
      />
    );
  }

  if (error && !loading) {
    return (
      <ProgramEditorErrorState
        subtitle="Configure programs and opportunities"
        title="Failed to load campaign programs."
        description="Please try again to reload this program editor."
        error={error}
        onRetry={() => void retry()}
        onBack={handleBack}
      />
    );
  }

  if (!loading && !campaign) {
    return (
      <ProgramEditorErrorState
        subtitle="Configure programs and opportunities"
        title="Campaign data not found."
        description="The selected campaign could not be resolved from the hierarchy response."
        error={`Missing campaign data for campaignId "${resolvedCampaignId}".`}
        onRetry={() => void retry()}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="min-h-full bg-[linear-gradient(180deg,#F7FBF9_0%,#FFFFFF_22%)]">
      <FormHeader
        onBack={handleBack}
        backLabel="Campaigns"
        title="Program Editor"
        titleIcon={<FolderTree className="h-5 w-5 text-[#00A86B]" />}
        subtitle={campaign ? `Campaign: ${campaign.name}` : "Configure programs and opportunities"}
      />

      <div className="mx-auto max-w-7xl space-y-6 px-6 py-6">
        <ProgramSection
          campaignId={resolvedCampaignId}
          selectedProgramId={selectedProgramId}
          onSelectProgram={setSelectedProgramId}
          onCreateProgram={(value) => handleAddProgram(value)}
          onUpdateProgram={handleUpdateProgram}
        />

        <OpportunitySection
          saving={opportunitySaving}
          selectedProgram={selectedProgram}
          expandedOpportunityId={expandedOpportunityId}
          onToggleOpportunityDetails={(opportunity) => {
            setExpandedOpportunityId((current) =>
              current === opportunity.id ? null : opportunity.id,
            );
          }}
          onCreateOpportunity={handleCreateOpportunity}
          onUpdateOpportunity={handleUpdateOpportunity}
        />
      </div>
    </div>
  );
}

export default UpdateProgramPage;
